import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { reelService } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ReelDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const [reel, setReel] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (id) {
      fetchReel();
    }
  }, [id]);

  const fetchReel = async () => {
    try {
      setLoading(true);
      const response = await reelService.get(parseInt(id!));
      setReel(response.data);
    } catch (error) {
      console.error('Failed to fetch reel:', error);
      enqueueSnackbar('Failed to load reel details', { variant: 'error' });
      navigate('/reels');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessReel = async () => {
    try {
      setProcessing(true);
      await reelService.process(parseInt(id!));
      enqueueSnackbar('Reel processing started', { variant: 'info' });
      
      // Poll for updates
      const pollInterval = setInterval(async () => {
        const response = await reelService.get(parseInt(id!));
        setReel(response.data);
        
        if (response.data.status === 'completed' || response.data.status === 'failed') {
          clearInterval(pollInterval);
          setProcessing(false);
          
          if (response.data.status === 'completed') {
            enqueueSnackbar('Reel processing completed!', { variant: 'success' });
          } else {
            enqueueSnackbar('Reel processing failed', { variant: 'error' });
          }
        }
      }, 3000);
    } catch (error) {
      console.error('Failed to process reel:', error);
      enqueueSnackbar('Failed to start processing', { variant: 'error' });
      setProcessing(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await reelService.download(parseInt(id!));
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reel.title}.mp4`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download reel:', error);
      enqueueSnackbar('Failed to download reel', { variant: 'error' });
    }
  };

  const handleRegenerateScript = async () => {
    try {
      setProcessing(true);
      const response = await reelService.regenerateScript(parseInt(id!));
      setReel(response.data);
      enqueueSnackbar('Script regenerated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to regenerate script:', error);
      enqueueSnackbar('Failed to regenerate script', { variant: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'failed':
        return 'error';
      case 'script_generated':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!reel) {
    return null;
  }

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/reels')}
              sx={{ mr: 2 }}
            >
              Back to Reels
            </Button>
            <Typography variant="h4" component="h1">
              {reel.title}
            </Typography>
          </Box>
          
          <Box>
            {reel.status === 'script_generated' && (
              <Button
                variant="contained"
                startIcon={processing ? <CircularProgress size={20} /> : <PlayIcon />}
                onClick={handleProcessReel}
                disabled={processing}
                sx={{ mr: 1 }}
              >
                {processing ? 'Processing...' : 'Process Video'}
              </Button>
            )}
            {reel.status === 'completed' && reel.video_url && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                sx={{ mr: 1 }}
              >
                Download
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchReel}
              disabled={loading || processing}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            {reel.video_url ? (
              <Card>
                <CardMedia
                  component="video"
                  controls
                  src={reel.video_url}
                  sx={{ width: '100%' }}
                />
              </Card>
            ) : reel.thumbnail_url ? (
              <Card>
                <CardMedia
                  component="img"
                  image={reel.thumbnail_url}
                  alt={reel.title}
                  sx={{ width: '100%', height: 'auto' }}
                />
              </Card>
            ) : (
              <Paper
                sx={{
                  aspectRatio: '16/9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                }}
              >
                <Box textAlign="center">
                  <PlayIcon sx={{ fontSize: 64, color: 'grey.400' }} />
                  <Typography color="text.secondary">
                    {reel.status === 'processing' ? 'Video is processing...' : 'No video available'}
                  </Typography>
                </Box>
              </Paper>
            )}
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Details
                </Typography>
                
                <Box mb={2}>
                  <Chip
                    label={getStatusLabel(reel.status)}
                    color={getStatusColor(reel.status)}
                    size="small"
                  />
                </Box>
                
                <Box mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Project
                  </Typography>
                  <Typography variant="body1">
                    {reel.project?.name || 'N/A'}
                  </Typography>
                </Box>
                
                <Box mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Language
                  </Typography>
                  <Typography variant="body1">
                    {reel.language.toUpperCase()}
                  </Typography>
                </Box>
                
                <Box mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1">
                    {reel.duration} seconds
                  </Typography>
                </Box>
                
                <Box mb={1}>
                  <Typography variant="body2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(reel.created_at), 'MMM d, yyyy h:mm a')}
                  </Typography>
                </Box>
                
                {reel.style && (
                  <Box mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Style
                    </Typography>
                    <Typography variant="body1">
                      {reel.style}
                    </Typography>
                  </Box>
                )}
                
                {reel.tone && (
                  <Box mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      Tone
                    </Typography>
                    <Typography variant="body1">
                      {reel.tone}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Paper>
              <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                variant="fullWidth"
              >
                <Tab label="Script" />
                <Tab label="Settings" />
                <Tab label="Analytics" />
              </Tabs>
              
              <Box px={3}>
                <TabPanel value={tabValue} index={0}>
                  {reel.script ? (
                    <>
                      <Box mb={3}>
                        <Typography variant="h6" gutterBottom>
                          Hook
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {reel.script.hook}
                        </Typography>
                      </Box>
                      
                      <Box mb={3}>
                        <Typography variant="h6" gutterBottom>
                          Main Content
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {reel.script.main_content}
                        </Typography>
                      </Box>
                      
                      <Box mb={3}>
                        <Typography variant="h6" gutterBottom>
                          Call to Action
                        </Typography>
                        <Typography variant="body1" paragraph>
                          {reel.script.call_to_action}
                        </Typography>
                      </Box>
                      
                      {reel.script.hashtags && reel.script.hashtags.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="h6" gutterBottom>
                            Hashtags
                          </Typography>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {reel.script.hashtags.map((tag: string) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRegenerateScript}
                        disabled={processing}
                      >
                        Regenerate Script
                      </Button>
                    </>
                  ) : (
                    <Typography color="text.secondary">
                      No script available
                    </Typography>
                  )}
                </TabPanel>
                
                <TabPanel value={tabValue} index={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Voice
                      </Typography>
                      <Typography variant="body1">
                        {reel.voice_id || 'Not set'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Target Audience
                      </Typography>
                      <Typography variant="body1">
                        {reel.target_audience || 'Not specified'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Include Captions
                      </Typography>
                      <Typography variant="body1">
                        {reel.include_captions ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Include Music
                      </Typography>
                      <Typography variant="body1">
                        {reel.include_music ? 'Yes' : 'No'}
                      </Typography>
                    </Grid>
                    
                    {reel.keywords && reel.keywords.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Keywords
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {reel.keywords.map((keyword: string) => (
                            <Chip key={keyword} label={keyword} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </TabPanel>
                
                <TabPanel value={tabValue} index={2}>
                  <Typography color="text.secondary">
                    Analytics data will be available after publishing
                  </Typography>
                </TabPanel>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default ReelDetail;